import React from 'react'
import { Button } from '@quickmd-llc/design-system'
import { type WaitingRoomV2VisitListItemDoctorApiOutContract } from 'api/generated/pwrdbyQuickMDProvidersApi.schemas'
import { PatientRow } from './PatientRow'

type Props = {
  list: WaitingRoomV2VisitListItemDoctorApiOutContract[]
  onStartVisit: (visit: WaitingRoomV2VisitListItemDoctorApiOutContract) => void
  onContinueVisit: (visit: WaitingRoomV2VisitListItemDoctorApiOutContract) => void
  /** VisitIds that were just deferred and should optimistically render "Start" (instead of
   * "Continue") until the eventually-consistent backend list reflects the release. */
  optimisticStartIds?: Set<string> | undefined
  /** True while a claim/start is in flight — disables every Start button so a rapid
   * double-click can't fire a second claim before the list refetch settles. */
  isStarting?: boolean
}

// An async visit already in progress AND assigned to the current provider comes back with
// Status 'pending' (backend collapses 'inprogress' -> 'pending', and such a visit only appears
// in its assigned provider's waiting room). Those get a "Continue" button that resumes the visit
// without re-claiming. While any visit is in progress, every other row's Start is disabled.
const isInProgress = (visit: WaitingRoomV2VisitListItemDoctorApiOutContract) => visit.Status === 'pending'

export const OnlineVisitsSection: React.FC<Props> = ({
  list,
  onStartVisit,
  onContinueVisit,
  optimisticStartIds,
  isStarting = false,
}) => {
  // A row that was just deferred is masked back to "Start" immediately, and is no longer counted
  // as in-progress (so it can't keep the other rows' Start buttons disabled).
  const showAsInProgress = (visit: WaitingRoomV2VisitListItemDoctorApiOutContract) =>
    isInProgress(visit) && !optimisticStartIds?.has(visit.VisitId)
  const hasInProgress = list.some(showAsInProgress)

  return (
    <section className='tailwind-scoped'>
      <div className='overflow-hidden rounded-[8px] border border-border-medium bg-white'>
        {list.map((visit) =>
          showAsInProgress(visit) ?
            <PatientRow
              key={visit.VisitId}
              visit={visit}
              trailing={
                <Button
                  size='s'
                  priority='primary'
                  onClick={(e: React.MouseEvent) => {
                    e.stopPropagation()
                    onContinueVisit(visit)
                  }}>
                  Continue
                </Button>
              }
            />
          : <PatientRow
              key={visit.VisitId}
              visit={visit}
              trailing={
                <Button
                  size='s'
                  priority='primary'
                  disabled={hasInProgress || isStarting}
                  onClick={(e: React.MouseEvent) => {
                    e.stopPropagation()
                    onStartVisit(visit)
                  }}>
                  Start
                </Button>
              }
            />,
        )}
      </div>
    </section>
  )
}
